/**
 * PhaseSection — one of the four CMS program-audit phases that frame the
 * audit workspace.
 *
 * Two modes:
 *  - Legacy accordion (default): a collapsible group; the current phase starts
 *    expanded and auto-expands when the audit advances into it. Collapsing is a
 *    pure view concern — nothing inside is gated.
 *  - railMode: navigation is handled by the left PhaseRail, so this renders as
 *    a plain phase header (title + explainer, no chevron, no numbered pill) with
 *    its panels always shown. The parent controls which phase is visible, so a
 *    phase's panels stay MOUNTED even when hidden — state is never lost on a
 *    phase switch. Same children, same props, either way.
 */
import React, { useState, useEffect } from 'react';

export default function PhaseSection({ number, title, explainer, current, lean, railMode, children }) {
  const [open, setOpen] = useState(!!current);
  // When the audit advances into this phase, open it — but never close a
  // phase the user chose to keep open. (Accordion mode only.)
  useEffect(() => { if (current && !railMode) setOpen(true); }, [current, railMode]);

  if (railMode) {
    return (
      <section>
        <div className="mb-3">
          <div className="text-base font-bold text-slate-800">{title}</div>
          {explainer && <div className="text-[12px] text-slate-400 mt-0.5 max-w-[62ch]">{explainer}</div>}
        </div>
        <div className="space-y-4">{children}</div>
      </section>
    );
  }

  return (
    <section>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-start gap-3 text-left py-1">
        {/* Focused audits are not framed as the four CMS phases — the numbered
            pill is comprehensive-only, so a lean review reads as a simple flow. */}
        {!lean && (
          <span className={`mt-0.5 flex-shrink-0 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${current ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
            Phase {number}
          </span>
        )}
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-slate-800">
            {title}
            {current && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-orange-600">You are here</span>}
          </span>
          <span className="block text-[11px] text-slate-400">{explainer}</span>
        </span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 mt-1 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && <div className="space-y-4 mt-2 ml-2 pl-3 border-l-2 border-slate-100">{children}</div>}
    </section>
  );
}
